import React, { useId, useMemo, useState } from 'react';
import {
    Combobox,
    ComboboxButton,
    ComboboxInput,
    ComboboxOption,
    ComboboxOptions,
} from '@headlessui/react';
import {
    COUNTRY_SEED,
    findCountry,
    type SupportedCountryIso,
} from '../../lib/countries.seed';
import styles from './CountrySelect.module.css';

interface CountrySelectProps {
    /** Visible label, already localized. */
    label: string;
    /** Selected ISO-3166-1 alpha-2 code. */
    value: SupportedCountryIso;
    /** Called with the new ISO when the user changes selection. */
    onChange: (iso: SupportedCountryIso) => void;
    /** Disable the combobox entirely (e.g. while submitting). */
    disabled?: boolean;
    /** UI locale — drives the country name displayed and the field direction. */
    locale: 'en' | 'ar';
    /** Validation feedback styling. */
    feedback?: 'error' | 'warning' | 'success';
    /** Human-readable validation message under the combobox. */
    feedbackMessage?: string;
    /** Optional id override (defaults to a `useId`-generated one). */
    id?: string;
    /** Optional name attribute. */
    name?: string;
    /** Localized "Search country..." placeholder for the input. */
    searchPlaceholder?: string;
    /** Localized "No countries match" empty-state copy. */
    noResultsLabel?: string;
}

/**
 * Booksy-parity searchable country picker (CR-2026-05-05b Q2).
 *
 * Backed by `COUNTRY_SEED`. User clicks the field → search input + filtered
 * list appears. Filter matches against EN name, AR name, and dial code (a
 * typed `+971` still resolves to UAE per CR-2026-05-04b AC-3 muscle memory).
 *
 * Replaces the native `<select>` to fix the 189-country friction (typing-
 * ahead by single letter is unhelpful when scrolling through 30+ "United"
 * countries). Headless UI Combobox is ~15KB gzipped and ships accessibility
 * (ARIA combobox role, keyboard nav, screen reader announcement of filtered
 * results) + RTL out of the box.
 *
 * Bug:  AB#1513
 * PBI:  AB#1516
 * CR:   CR-2026-05-04b → CR-2026-05-05a → CR-2026-05-05b
 * Epic: AB#1512
 */
const CountrySelect: React.FC<CountrySelectProps> = ({
    label,
    value,
    onChange,
    disabled = false,
    locale,
    feedback,
    feedbackMessage,
    id,
    name,
    searchPlaceholder,
    noResultsLabel,
}) => {
    const generatedId = useId();
    const fieldId = id || `country-${generatedId}`;
    const feedbackId = feedbackMessage ? `${fieldId}-feedback` : undefined;

    const [query, setQuery] = useState('');
    const selected = findCountry(value);

    const filtered = useMemo(() => {
        const q = query.trim().toLowerCase();
        if (!q) return COUNTRY_SEED;
        return COUNTRY_SEED.filter((c) => {
            return (
                c.nameEn.toLowerCase().includes(q) ||
                c.nameAr.toLowerCase().includes(q) ||
                c.dialCode.toLowerCase().includes(q) ||
                c.iso2.toLowerCase().startsWith(q)
            );
        });
    }, [query]);

    const displayName = (iso: SupportedCountryIso | undefined) => {
        if (!iso) return '';
        const c = findCountry(iso);
        if (!c) return iso;
        return locale === 'ar' ? c.nameAr : c.nameEn;
    };

    return (
        <div className={styles.container}>
            <label htmlFor={fieldId} className={styles.label}>
                {label}
            </label>
            <Combobox
                value={value}
                onChange={(iso: SupportedCountryIso | null) => {
                    if (iso) onChange(iso);
                }}
                disabled={disabled}
            >
                <div
                    className={`${styles.comboboxRoot} ${disabled ? styles.disabled : ''} ${
                        feedback ? styles[feedback] : ''
                    }`}
                    dir={locale === 'ar' ? 'rtl' : 'ltr'}
                >
                    <ComboboxInput
                        id={fieldId}
                        name={name}
                        className={styles.input}
                        displayValue={(iso: SupportedCountryIso) =>
                            displayName(iso)
                        }
                        onChange={(e) => setQuery(e.target.value)}
                        // CR-2026-05-06 — select all on focus so the first
                        // keystroke replaces the displayed country name and
                        // gives the user clear feedback that the input is
                        // editable for search. Matches Apple Mail / Spotlight
                        // / Booksy convention.
                        onFocus={(e) => {
                            e.target.select();
                            setQuery('');
                        }}
                        placeholder={
                            searchPlaceholder ??
                            (locale === 'ar' ? 'ابحث عن دولة...' : 'Search country...')
                        }
                        aria-invalid={feedback === 'error' || undefined}
                        aria-describedby={feedbackId}
                        autoComplete="off"
                    />
                    <ComboboxButton className={styles.chevron} aria-label="toggle">
                        <svg
                            width="14"
                            height="14"
                            viewBox="0 0 14 14"
                            fill="none"
                            aria-hidden="true"
                        >
                            <path
                                d="M3 5l4 4 4-4"
                                stroke="currentColor"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </svg>
                    </ComboboxButton>
                </div>
                <ComboboxOptions
                    className={styles.options}
                    anchor={{ to: 'bottom start', gap: 4 }}
                >
                    {filtered.length === 0 ? (
                        <div className={styles.empty}>
                            {noResultsLabel ??
                                (locale === 'ar'
                                    ? 'لا توجد نتائج'
                                    : 'No countries match')}
                        </div>
                    ) : (
                        filtered.map((c) => (
                            <ComboboxOption
                                key={c.iso2}
                                value={c.iso2}
                                className={({ focus, selected: isSelected }) =>
                                    `${styles.option} ${focus ? styles.optionFocus : ''} ${
                                        isSelected ? styles.optionSelected : ''
                                    }`
                                }
                            >
                                <span
                                    className={`fi fi-${c.iso2Lower} ${styles.optionFlag}`}
                                    aria-hidden="true"
                                />
                                <span className={styles.optionName}>
                                    {locale === 'ar' ? c.nameAr : c.nameEn}
                                </span>
                                <span className={styles.optionDial}>
                                    {c.dialCode}
                                </span>
                            </ComboboxOption>
                        ))
                    )}
                </ComboboxOptions>
            </Combobox>
            {/* SR-only hidden input mirrors the value for any non-RHF consumers
                that probe the form by selector — keeps the form-field contract
                stable across the native→combobox swap. */}
            <input type="hidden" name={name} value={value} aria-hidden="true" />
            {feedbackMessage && (
                <div
                    id={feedbackId}
                    className={`${styles.feedback} ${feedback ? styles[feedback] : ''}`}
                    role="alert"
                >
                    {feedbackMessage}
                </div>
            )}
            {/* Selected country flag (visual confirmation) — surfaced inline
                above the combobox in RTL to match Booksy's pattern; absorbed
                into the combobox button styling otherwise. */}
            {selected && (
                <div className={styles.selectedSummary} aria-hidden="true">
                    <span className={`fi fi-${selected.iso2Lower}`} />
                    <span>{selected.dialCode}</span>
                </div>
            )}
        </div>
    );
};

export default CountrySelect;
